import React from "react";

function scoreColor(score) {
  if (score == null) return { fg: "#9CA3AF", bg: "#F3F4F6", ring: "#E5E7EB", label: "—" };
  if (score >= 76) return { fg: "#059669", bg: "#ECFDF5", ring: "#059669", label: "Strong" };
  if (score >= 41) return { fg: "#D97706", bg: "#FFFBEB", ring: "#D97706", label: "Fair" };
  return { fg: "#DC2626", bg: "#FEF2F2", ring: "#DC2626", label: "Weak" };
}

function confidenceLabel(confidence) {
  if (!confidence) return null;
  const map = {
    high: { text: "High", color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
    medium: { text: "Medium", color: "text-amber-600 bg-amber-50 border-amber-200" },
    low: { text: "Low", color: "text-red-500 bg-red-50 border-red-200" },
  };
  return map[confidence] || null;
}

export function MatchRing({ score, size = 72, stroke = 6, testid, confidence }) {
  const s = score ?? 0;
  const c = scoreColor(score);
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const dash = score == null ? 0 : (s / 100) * circ;

  return (
    <div className="relative inline-flex flex-col items-center gap-1" data-testid={testid}>
      <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} stroke="#E5E7EB" strokeWidth={stroke} fill="none" />
          {score != null && (
            <circle
              cx={size / 2} cy={size / 2} r={r}
              stroke={c.ring} strokeWidth={stroke} fill="none"
              strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="square"
              style={{ transition: "stroke-dasharray 600ms ease-out" }}
            />
          )}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
          <span className="font-mono font-black text-xl tracking-tight" style={{ color: score == null ? "#9CA3AF" : c.fg }}>
            {score == null ? "—" : s}
          </span>
          <span className="tick-label text-[8px] mt-0.5">{c.label}</span>
        </div>
      </div>
      {confidence && (
        <ConfidenceBadge confidence={confidence} />
      )}
    </div>
  );
}

export function ConfidenceBadge({ confidence }) {
  const conf = confidenceLabel(confidence);
  if (!conf) return null;
  return (
    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border ${conf.color}`}>
      {conf.text}
    </span>
  );
}

export function MatchBar({ score }) {
  const c = scoreColor(score);
  const s = score ?? 0;
  return (
    <div className="w-full">
      <div className="h-1.5 w-full bg-slate-50 relative">
        <div className="h-full transition-all duration-500" style={{ width: `${s}%`, background: c.fg }} />
      </div>
    </div>
  );
}

export function MatchPill({ score, confidence }) {
  const c = scoreColor(score);
  if (score == null) return null;
  return (
    <div className="inline-flex items-center gap-1.5 px-2 py-1 border" style={{ borderColor: c.fg + "40", background: c.bg }}>
      <span className="font-mono font-black text-sm" style={{ color: c.fg }}>{score}</span>
      <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: c.fg }}>{c.label}</span>
      {confidence && (
        <>
          <span className="w-px h-3 bg-border mx-0.5" />
          <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: c.fg }}>{confidence}</span>
        </>
      )}
    </div>
  );
}

export default MatchRing;
